import { GrocyConfig, storage } from "./storage.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// Types based on Grocy API schema
export interface ShoppingListItem {
  id: number;
  shopping_list_id: number;
  product_id: number;
  qu_id: number;
  note: string;
  amount: number;
  done: number;
  done_timestamp: string;
  row_created_timestamp: string;
  userfields: Record<string, any>;
}

export interface Product {
  id: number;
  name: string;
  description: string | null;
  product_group_id: number | null;
  parent_product_id: number | null;
  active: number;
  location_id: number;
  shopping_location_id: number | null;
  default_consume_location_id: number | null;
  qu_id_purchase: number;
  qu_id_stock: number;
  qu_id_consume: number;
  qu_id_price: number;
  min_stock_amount: number;
  no_own_stock: number;
  hide_on_stock_overview: number;
  default_best_before_days: number;
  default_best_before_days_after_open: number;
  default_best_before_days_after_freezing: number;
  default_best_before_days_after_thawing: number;
  due_type: number;
  quick_consume_amount: number;
  quick_open_amount: number;
  enable_tare_weight_handling: number;
  tare_weight: number;
  not_check_stock_fulfillment_for_recipes: number;
  cumulate_min_stock_amount_of_sub_products: number;
  treat_opened_as_out_of_stock: number;
  should_not_be_frozen: number;
  move_on_open: number;
  disable_open: number;
  default_stock_label_type: number;
  auto_reprint_stock_label: number;
  default_purchase_price_type: number;
  calories: number | null;
  picture_file_name: string | null;
  row_created_timestamp: string;
  userfields: Record<string, any>;
}

export interface ProductGroup {
  id: number;
  name: string;
  description: string;
  row_created_timestamp: string;
  userfields: Record<string, any>;
}

export interface QuantityUnit {
  id: number;
  name: string;
  name_plural: string;
  description: string;
  row_created_timestamp: string;
  plural_forms: string;
  userfields: Record<string, any>;
}

export interface QuantityUnitConversion {
  id: number;
  from_qu_id: number;
  to_qu_id: number;
  factor: number;
  product_id: number | null;
  row_created_timestamp: string;
}

export interface ShoppingListItemWithProduct extends ShoppingListItem {
  product?: Product;
  productGroup?: ProductGroup;
  quantityUnit?: QuantityUnit;
}

export interface GroupedItems {
  [key: string]: ShoppingListItemWithProduct[];
}

// New types for stock data
export interface CurrentStockResponse {
  product_id: number;
  amount: number;
  amount_aggregated: number;
  amount_opened: number;
  amount_opened_aggregated: number;
  best_before_date: string;
  is_aggregated_amount: boolean;
  product: ProductWithoutUserfields;
}

export interface ProductWithoutUserfields {
  id: number;
  name: string;
  description: string;
  location_id: number;
  qu_id_purchase: number;
  qu_id_stock: number;
  enable_tare_weight_handling: number;
  not_check_stock_fulfillment_for_recipes: number;
  product_group_id: number;
  tare_weight: number;
  min_stock_amount: number;
  default_best_before_days: number;
  default_best_before_days_after_open: number;
  picture_file_name: string;
  row_created_timestamp: string;
  shopping_location_id: number;
  treat_opened_as_out_of_stock: number;
  auto_reprint_stock_label: number;
  no_own_stock: number;
  should_not_be_frozen: number;
  default_consume_location_id: number;
  move_on_open: number;
}

export interface Location {
  id: number;
  name: string;
  description: string;
  row_created_timestamp: string;
  userfields: Record<string, any>;
}

export interface ProductItemWithDetails {
  stock: CurrentStockResponse;
  product: Product;
  quantityUnit: QuantityUnit;
  location: Location;
}

export interface Recipe {
  id: number;
  name: string;
  description: string | null;
  row_created_timestamp: string;
  picture_file_name: string | null;
  base_servings: number;
  desired_servings: number;
  not_check_shoppinglist: number;
  type: string;
  product_id: number | null;
}

export interface RecipeIngredient {
  id: number;
  recipe_id: number;
  product_id: number;
  amount: number;
  note: string | null;
  qu_id: number;
  only_check_single_unit_in_stock: number;
  ingredient_group: string | null;
  not_check_stock_fulfillment: number;
  row_created_timestamp: string;
  variable_amount: string | null;
  price_factor: number;
  round_up: number;
}

const getGrocyConfig = (): GrocyConfig => {
  const config = storage.getConfig();
  if (!config) {
    throw new Error("No Grocy configuration found. Please log in first.");
  }
  return config;
};

// Generic fetch function for Grocy API
export async function grocyFetch<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const config = getGrocyConfig();
  const response = await fetch(`${config.baseUrl}/api/${path}`, {
    headers: {
      "GROCY-API-KEY": config.apiKey,
      "Content-Type": "application/json",
    },
    ...options,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${path}: ${response.statusText}`);
  }

  return response.json();
}

// Generic update function for Grocy API
export async function grocyUpdate(path: string, data: any): Promise<void> {
  const config = getGrocyConfig();
  const response = await fetch(`${config.baseUrl}/api/${path}`, {
    method: "PUT",
    headers: {
      "GROCY-API-KEY": config.apiKey,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to update ${path}: ${response.statusText}`);
  }
}

export async function grocyCreate<T>(path: string, data: any): Promise<T> {
  const config = getGrocyConfig();
  const response = await fetch(`${config.baseUrl}/api/${path}`, {
    method: "POST",
    headers: {
      "GROCY-API-KEY": config.apiKey,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create ${path}: ${response.statusText}`);
  }

  return response.json();
}

// API functions using the generic fetch functions
export const fetchShoppingList = (): Promise<ShoppingListItem[]> =>
  grocyFetch<ShoppingListItem[]>("objects/shopping_list");

export const fetchProducts = (): Promise<Product[]> =>
  grocyFetch<Product[]>("objects/products");

export const fetchProductGroups = (): Promise<ProductGroup[]> =>
  grocyFetch<ProductGroup[]>("objects/product_groups");

export const fetchQuantityUnits = (): Promise<QuantityUnit[]> =>
  grocyFetch<QuantityUnit[]>("objects/quantity_units");

export const fetchQuantityUnitConversions = (): Promise<
  QuantityUnitConversion[]
> => grocyFetch<QuantityUnitConversion[]>("objects/quantity_unit_conversions");

export const fetchRecipes = (): Promise<Recipe[]> =>
  grocyFetch<Recipe[]>("objects/recipes");

export const fetchRecipeIngredients = (): Promise<RecipeIngredient[]> =>
  grocyFetch<RecipeIngredient[]>("objects/recipes_pos");

export const updateShoppingListItem = async (
  { itemId, done }: { itemId: number; done: number },
): Promise<void> => {
  await grocyUpdate(`objects/shopping_list/${itemId}`, { done });
};

export const updateProductAisle = async (
  { productId, aisle }: { productId: number; aisle: string },
): Promise<void> => {
  await grocyUpdate(`userfields/products/${productId}`, { aisle });
};

export const useShoppingListItemMutations = () => {
  const queryClient = useQueryClient();

  const markAsDoneMutation = useMutation({
    mutationFn: (itemId: number) => updateShoppingListItem({ itemId, done: 1 }),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: ["shopping-list"] });
      const previousItems = queryClient.getQueryData<ShoppingListItem[]>([
        "shopping-list",
      ]);

      queryClient.setQueryData<ShoppingListItem[]>(["shopping-list"], (old) => {
        if (!old) return old;
        return old.map((item) =>
          item.id === itemId
            ? { ...item, done: 1, done_timestamp: new Date().toISOString() }
            : item
        );
      });

      return { previousItems };
    },
    onError: (err, itemId, context) => {
      console.error("Error marking item as done:", err, "Item ID:", itemId);
      if (context?.previousItems) {
        queryClient.setQueryData(["shopping-list"], context.previousItems);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
    },
  });

  const markAsUndoneMutation = useMutation({
    mutationFn: (itemId: number) => updateShoppingListItem({ itemId, done: 0 }),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: ["shopping-list"] });
      const previousItems = queryClient.getQueryData<ShoppingListItem[]>([
        "shopping-list",
      ]);

      queryClient.setQueryData<ShoppingListItem[]>(["shopping-list"], (old) => {
        if (!old) return old;
        return old.map((item) =>
          item.id === itemId ? { ...item, done: 0, done_timestamp: "" } : item
        );
      });

      return { previousItems };
    },
    onError: (err, itemId, context) => {
      console.error("Error marking item as undone:", err, "Item ID:", itemId);
      if (context?.previousItems) {
        queryClient.setQueryData(["shopping-list"], context.previousItems);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
    },
  });

  return {
    markAsDoneMutation,
    markAsUndoneMutation,
  };
};

// Hook for product mutations
export const useProductMutations = (onSettled?: () => void) => {
  const queryClient = useQueryClient();

  const updateAisleMutation = useMutation({
    mutationFn: ({ productId, aisle }: { productId: number; aisle: string }) =>
      updateProductAisle({ productId, aisle }),
    onMutate: async ({ productId, aisle }) => {
      await queryClient.cancelQueries({ queryKey: ["products"] });
      const previousProducts = queryClient.getQueryData<Product[]>([
        "products",
      ]);

      queryClient.setQueryData<Product[]>(["products"], (old) => {
        if (!old) return old;
        return old.map((product) =>
          product.id === productId
            ? {
              ...product,
              userfields: {
                ...product.userfields,
                aisle,
              },
            }
            : product
        );
      });

      return { previousProducts };
    },
    onError: (err, { productId }, context) => {
      console.error(
        "Error updating product aisle:",
        err,
        "Product ID:",
        productId,
      );
      if (context?.previousProducts) {
        queryClient.setQueryData(["products"], context.previousProducts);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      onSettled?.();
    },
  });

  return {
    updateAisleMutation,
  };
};

// New API functions for stock data
export const fetchCurrentStock = (): Promise<CurrentStockResponse[]> =>
  grocyFetch<CurrentStockResponse[]>("stock");

export const fetchLocations = (): Promise<Location[]> =>
  grocyFetch<Location[]>("objects/locations");

// Product barcodes interface
export interface ProductBarcode {
  barcode: string;
  product_id: number;
}

export const fetchProductBarcodes = (): Promise<ProductBarcode[]> =>
  grocyFetch<ProductBarcode[]>("objects/product_barcodes_view");

// User settings interface
export interface UserSettings {
  night_mode: string;
  auto_night_mode_enabled: boolean;
  auto_night_mode_time_range_from: string;
  auto_night_mode_time_range_to: string;
  auto_night_mode_time_range_goes_over_midnight: boolean;
  night_mode_enabled_internal: string;
  auto_reload_on_db_change: boolean;
  show_clock_in_header: boolean;
  keep_screen_on: boolean;
  keep_screen_on_when_fullscreen_card: boolean;
  product_presets_location_id: string;
  product_presets_product_group_id: string;
  product_presets_qu_id: string;
  product_presets_default_due_days: number;
  product_presets_treat_opened_as_out_of_stock: string;
  product_presets_default_stock_label_type: number;
  stock_decimal_places_amounts: string;
  stock_decimal_places_prices_input: string;
  stock_decimal_places_prices_display: string;
  stock_auto_decimal_separator_prices: boolean;
  stock_due_soon_days: string;
  stock_default_purchase_amount: string;
  stock_default_consume_amount: string;
  stock_default_consume_amount_use_quick_consume_amount: string;
  scan_mode_consume_enabled: boolean;
  scan_mode_purchase_enabled: string;
  show_icon_on_stock_overview_page_when_product_is_on_shopping_list: string;
  stock_overview_show_all_out_of_stock_products: boolean;
  show_purchased_date_on_purchase: boolean;
  show_warning_on_purchase_when_due_date_is_earlier_than_next: boolean;
  shopping_list_to_stock_workflow_auto_submit_when_prefilled: boolean;
  shopping_list_show_calendar: boolean;
  shopping_list_round_up: boolean;
  shopping_list_auto_add_below_min_stock_amount: string;
  shopping_list_auto_add_below_min_stock_amount_list_id: string;
  shopping_list_print_show_header: boolean;
  shopping_list_print_group_by_product_group: boolean;
  shopping_list_print_layout_type: string;
  recipe_ingredients_group_by_product_group: boolean;
  recipes_show_list_side_by_side: boolean;
  recipes_show_ingredient_checkbox: boolean;
  chores_due_soon_days: number;
  chores_overview_swap_tracking_buttons: boolean;
  batteries_due_soon_days: number;
  tasks_due_soon_days: number;
  calendar_color_products: string;
  calendar_color_tasks: string;
  calendar_color_chores: string;
  calendar_color_batteries: string;
  calendar_color_meal_plan: string;
  datatables_rowGroup_shoppinglist_table: string;
  [key: string]: any; // For any additional settings
}

export const fetchUserSettings = (): Promise<UserSettings> =>
  grocyFetch<UserSettings>("user/settings");

export const fetchProductImage = async (
  product: Product,
  width: number = 400,
): Promise<string | null> => {
  const config = getGrocyConfig();
  if (!config || !product.picture_file_name) return null;

  try {
    // Base64 encode the picture filename
    const encodedFilename = btoa(product.picture_file_name);

    // Grocy product image URL pattern with Base64 encoded filename
    const imageUrl =
      `${config.baseUrl}/api/files/productpictures/${encodedFilename}?force_serve_as=picture&best_fit_width=${width}`;

    const response = await fetch(imageUrl, {
      headers: {
        "GROCY-API-KEY": config.apiKey,
      },
    });

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.warn("Failed to fetch product image:", error);
    return null;
  }
};

// Upload product image from external URL
export const uploadProductImage = async (
  imageUrl: string,
  baseFilename: string,
): Promise<string> => {
  const config = getGrocyConfig();

  try {
    // First, fetch the image from the external URL
    const imageResponse = await fetch(imageUrl);
    if (!imageResponse.ok) {
      throw new Error(`Failed to fetch image from ${imageUrl}`);
    }

    const imageBlob = await imageResponse.blob();

    // Determine file extension based on content type
    let extension = ".jpg"; // default
    const contentType = imageBlob.type ||
      imageResponse.headers.get("content-type");

    if (contentType) {
      const mimeToExtension: Record<string, string> = {
        "image/jpeg": ".jpg",
        "image/jpg": ".jpg",
        "image/png": ".png",
        "image/gif": ".gif",
        "image/webp": ".webp",
        "image/bmp": ".bmp",
        "image/tiff": ".tiff",
        "image/tif": ".tif",
      };

      extension = mimeToExtension[contentType.toLowerCase()] || ".jpg";
    }

    // If no content type, try to infer from URL
    if (extension === ".jpg" && imageUrl.includes(".")) {
      const urlExtension = imageUrl.split(".").pop()?.toLowerCase();
      if (
        urlExtension &&
        ["png", "gif", "webp", "bmp", "tiff", "tif"].includes(urlExtension)
      ) {
        extension = `.${urlExtension}`;
      }
    }

    const filename = `${baseFilename}${extension}`;

    // Base64 encode the filename
    const encodedFilename = btoa(filename);

    // Upload to Grocy using PUT with encoded filename
    const uploadResponse = await fetch(
      `${config.baseUrl}/api/files/productpictures/${encodedFilename}`,
      {
        method: "PUT",
        headers: {
          "GROCY-API-KEY": config.apiKey,
          "Content-Type": imageBlob.type || "image/jpeg",
        },
        body: imageBlob,
      },
    );

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload image: ${uploadResponse.statusText}`);
    }

    return filename; // Return the actual filename used
  } catch (error) {
    console.warn("Failed to upload product image:", error);
    throw error;
  }
};

// Consume (decrement) stock for a product by 1, return transaction_id
export const consumeProductStock = async (
  { productId, amount = 1 }: { productId: number; amount?: number },
): Promise<string> => {
  const config = getGrocyConfig();
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), 30_000);
  let response: Response;

  try {
    response = await fetch(
      `${config.baseUrl}/api/stock/products/${productId}/consume`,
      {
        method: "POST",
        signal: abortController.signal,
        headers: {
          "GROCY-API-KEY": config.apiKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount,
          transaction_type: "consume",
          spoiled: false,
        }),
      },
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error(`Timed out consuming stock for product ${productId}`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const responseText = await response.text().catch(() => "");
    const details = responseText ? ` ${responseText}` : "";
    throw new Error(
      `Failed to consume stock for product ${productId}: ${response.status} ${response.statusText}${details}`,
    );
  }
  const data = await response.json();
  // The response is an array of StockLogEntry, all with the same transaction_id
  if (Array.isArray(data) && data.length > 0 && data[0].transaction_id) {
    return data[0].transaction_id;
  }
  throw new Error("No transaction_id returned from consume");
};

// Undo a stock transaction by transactionId
export const undoStockTransaction = async (
  transactionId: string,
): Promise<void> => {
  const config = getGrocyConfig();
  const response = await fetch(
    `${config.baseUrl}/api/stock/transactions/${transactionId}/undo`,
    {
      method: "POST",
      headers: {
        "GROCY-API-KEY": config.apiKey,
        "Content-Type": "application/json",
      },
    },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to undo stock transaction ${transactionId}: ${response.statusText}`,
    );
  }
};

// Add (increment) stock for a product by 1 (for undo)
export const addProductStock = async (
  { productId, amount = 1 }: { productId: number; amount?: number },
): Promise<void> => {
  const config = getGrocyConfig();
  const response = await fetch(
    `${config.baseUrl}/api/stock/products/${productId}/add`,
    {
      method: "POST",
      headers: {
        "GROCY-API-KEY": config.apiKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ amount, transaction_type: "purchase" }),
    },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to add stock for product ${productId}: ${response.statusText}`,
    );
  }
};

// Add these types after the existing interfaces
export interface ExternalBarcodeLookupResponse {
  name: string;
  location_id: number;
  qu_id_purchase: number;
  qu_id_stock: number;
  qu_factor_purchase_to_stock: number;
  __barcode: string;
  id?: number; // Only if product was created
  __image_url?: string; // External image URL
}

export interface CreateProductRequest {
  name: string;
  location_id: number;
  product_group_id: number;
  qu_id_purchase?: number;
  qu_id_stock?: number;
  qu_factor_purchase_to_stock?: number;
  parent_product_id?: number;
  picture_file_name?: string;
  no_own_stock?: number;
}

export interface AddStockRequest {
  amount: number;
  best_before_date?: string;
  location_id?: number;
  transaction_type: "purchase";
}

// Add these functions after the existing functions

// External barcode lookup
export const lookupExternalBarcode = async (
  barcode: string,
  add: boolean = false,
): Promise<ExternalBarcodeLookupResponse | null> => {
  const config = getGrocyConfig();
  const url = `${config.baseUrl}/api/stock/barcodes/external-lookup/${barcode}${
    add ? "?add=true" : ""
  }`;

  const response = await fetch(url, {
    headers: {
      "GROCY-API-KEY": config.apiKey,
    },
  });

  if (!response.ok) {
    throw new Error(`External barcode lookup failed: ${response.statusText}`);
  }

  return response.json();
};

// Utility function for barcode normalization
export const normalizeBarcode = async (
  barcode: string,
  add: boolean = false,
): Promise<ExternalBarcodeLookupResponse | null> => {
  // Try original barcode first
  let response = await lookupExternalBarcode(barcode, add);

  // If no result, try with "0" prepended
  if (!response) {
    const barcodeWithZero = "0" + barcode;
    response = await lookupExternalBarcode(barcodeWithZero, add);
  }

  return response;
};

// Create new product
export const createProduct = async (
  productData: CreateProductRequest,
): Promise<{ created_object_id: number }> => {
  const config = getGrocyConfig();
  const response = await fetch(`${config.baseUrl}/api/objects/products`, {
    method: "POST",
    headers: {
      "GROCY-API-KEY": config.apiKey,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(productData),
  });

  if (!response.ok) {
    throw new Error(`Failed to create product: ${response.statusText}`);
  }

  return response.json();
};

// Update existing product
export const updateProduct = async (
  productId: number,
  productData: Partial<CreateProductRequest>,
): Promise<void> => {
  const config = getGrocyConfig();
  const response = await fetch(
    `${config.baseUrl}/api/objects/products/${productId}`,
    {
      method: "PUT",
      headers: {
        "GROCY-API-KEY": config.apiKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(productData),
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to update product: ${response.statusText}`);
  }
};

// Add barcode to product
export const addProductBarcode = async (
  productId: number,
  barcode: string,
): Promise<void> => {
  const config = getGrocyConfig();
  const response = await fetch(
    `${config.baseUrl}/api/objects/product_barcodes`,
    {
      method: "POST",
      headers: {
        "GROCY-API-KEY": config.apiKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        product_id: productId,
        barcode: barcode,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to add barcode: ${response.statusText}`);
  }
};

// Enhanced addProductStock function for purchase
export const addStockToProduct = async (
  productId: number,
  stockData: AddStockRequest,
): Promise<void> => {
  const config = getGrocyConfig();
  const response = await fetch(
    `${config.baseUrl}/api/stock/products/${productId}/add`,
    {
      method: "POST",
      headers: {
        "GROCY-API-KEY": config.apiKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(stockData),
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to add stock: ${response.statusText}`);
  }
};

// Clear done shopping list items
export const clearDoneShoppingListItems = async (): Promise<void> => {
  const config = getGrocyConfig();
  const response = await fetch(
    `${config.baseUrl}/api/stock/shoppinglist/clear`,
    {
      method: "POST",
      headers: {
        "GROCY-API-KEY": config.apiKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ done_only: true }),
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to clear done shopping list items: ${response.statusText}`,
    );
  }
};

// Hook for clearing done shopping list items
export const useClearDoneItemsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clearDoneShoppingListItems,
    onSuccess: () => {
      // Invalidate and refetch shopping list data
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
    },
    onError: (error) => {
      console.error("Error clearing done shopping list items:", error);
    },
  });
};

// Types for shopping list operations
export interface AddToShoppingListRequest {
  product_id: number;
  product_amount?: number;
  list_id?: number;
  qu_id?: number;
  note?: string;
}

export interface AddSelectedRecipeIngredientRequest {
  product_id: number;
  product_amount: number;
  qu_id?: number;
}

export interface AddSelectedRecipeIngredientsRequest {
  recipeId: number;
  recipeName: string;
  items: AddSelectedRecipeIngredientRequest[];
}

// Add product to shopping list
export const addProductToShoppingList = async (
  data: AddToShoppingListRequest,
): Promise<void> => {
  const config = getGrocyConfig();
  const response = await fetch(
    `${config.baseUrl}/api/stock/shoppinglist/add-product`,
    {
      method: "POST",
      headers: {
        "GROCY-API-KEY": config.apiKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to add product to shopping list: ${response.statusText}`,
    );
  }
};

export const getRecipeShoppingListNote = (
  recipeId: number,
  recipeName: string,
): string => `Recipe: ${recipeName} (#recipe:${recipeId})`;

export const createShoppingListItem = async (
  data: AddToShoppingListRequest & { shopping_list_id?: number },
): Promise<{ created_object_id: number }> =>
  grocyCreate<{ created_object_id: number }>("objects/shopping_list", {
    shopping_list_id: data.shopping_list_id ?? data.list_id ?? 1,
    product_id: data.product_id,
    amount: data.product_amount ?? 1,
    qu_id: data.qu_id,
    note: data.note ?? "",
  });

export const addSelectedRecipeIngredientsToShoppingList = async (
  request: AddSelectedRecipeIngredientsRequest,
): Promise<void> => {
  const { items, recipeId, recipeName } = request;

  if (items.length === 0) {
    return;
  }

  const mergedItems = new Map<number, AddSelectedRecipeIngredientRequest>();

  for (const item of items) {
    const existing = mergedItems.get(item.product_id);
    mergedItems.set(item.product_id, {
      product_id: item.product_id,
      product_amount: (existing?.product_amount || 0) + item.product_amount,
      qu_id: existing?.qu_id ?? item.qu_id,
    });
  }

  const note = getRecipeShoppingListNote(recipeId, recipeName);

  await Promise.all(
    Array.from(mergedItems.values()).map((item) =>
      createShoppingListItem({
        product_id: item.product_id,
        product_amount: item.product_amount,
        qu_id: item.qu_id,
        note,
      })
    ),
  );
};

// Hook for adding items to shopping list
export const useAddToShoppingListMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addProductToShoppingList,
    onSuccess: () => {
      // Invalidate and refetch shopping list data
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
    },
    onError: (error) => {
      console.error("Error adding product to shopping list:", error);
    },
  });
};

export const useAddSelectedRecipeIngredientsToShoppingListMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addSelectedRecipeIngredientsToShoppingList,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
    },
    onError: (error) => {
      console.error(
        "Error adding selected recipe ingredients to shopping list:",
        error,
      );
    },
  });
};

// Combined hook for creating new product and adding to shopping list
export const useCreateProductAndAddToListMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ productData, amount, listId }: {
      productData: CreateProductRequest & { no_own_stock?: boolean };
      amount: number;
      listId?: number;
    }) => {
      // Prepare product data with proper types
      const finalProductData: CreateProductRequest = {
        ...productData,
        no_own_stock: productData.no_own_stock ? 1 : 0,
      };

      // Step 1: Create the product
      const createResponse = await createProduct(finalProductData);
      const productId = createResponse.created_object_id;

      // Step 2: Add to shopping list
      await addProductToShoppingList({
        product_id: productId,
        product_amount: amount,
        list_id: listId,
        qu_id: finalProductData.qu_id_purchase,
      });

      return { productId };
    },
    onSuccess: () => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error) => {
      console.error(
        "Error creating product and adding to shopping list:",
        error,
      );
    },
  });
};

// Recipe actions
export const addRecipeToShoppingList = async (
  recipeId: number,
): Promise<void> => {
  const config = getGrocyConfig();
  const response = await fetch(
    `${config.baseUrl}/api/recipes/${recipeId}/add-not-fulfilled-products-to-shoppinglist`,
    {
      method: "POST",
      headers: {
        "GROCY-API-KEY": config.apiKey,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to add recipe to shopping list: ${response.statusText}`,
    );
  }
};

export const consumeRecipe = async (recipeId: number): Promise<void> => {
  const config = getGrocyConfig();
  const response = await fetch(
    `${config.baseUrl}/api/recipes/${recipeId}/consume`,
    {
      method: "POST",
      headers: {
        "GROCY-API-KEY": config.apiKey,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to consume recipe: ${response.statusText}`);
  }
};

// Hook for recipe mutations
export const useRecipeMutations = () => {
  const queryClient = useQueryClient();

  const addToShoppingListMutation = useMutation({
    mutationFn: addRecipeToShoppingList,
    onSuccess: () => {
      // Invalidate and refetch shopping list data
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
    },
    onError: (error) => {
      console.error("Error adding recipe to shopping list:", error);
    },
  });

  const consumeRecipeMutation = useMutation({
    mutationFn: consumeRecipe,
    onSuccess: () => {
      // Invalidate and refetch stock data
      queryClient.invalidateQueries({ queryKey: ["stock"] });
    },
    onError: (error) => {
      console.error("Error consuming recipe:", error);
    },
  });

  return {
    addToShoppingListMutation,
    consumeRecipeMutation,
  };
};
